/**
 * Reading the outcome of a publish run from its per-account tasks.
 *
 * Kept separate from the dialog so the counting rules — which decide what the
 * user is told happened — are testable on their own.
 */

const TERMINAL = ["success", "fail"];

/**
 * Whether a task reached a state that will not change on its own.
 *
 * TikTok answers `success` while the video is still being processed on their
 * side; the cron re-polls it, so from the UI's point of view it is not done.
 *
 * @param {Object} task - Post task.
 * @returns {boolean} True when the task will not change without another action.
 */
export function isTaskDone(task) {
  if (!task || !TERMINAL.includes(task.status)) return false;
  if (task.social_type === "tiktok" && task.result?.pending === true) return false;
  return true;
}

/**
 * Whether a task ended badly.
 *
 * A task can carry `status: success` from the queue while the provider reported
 * a failure in the payload, so both are checked.
 *
 * @param {Object} task - Post task.
 * @returns {boolean} True when the task failed.
 */
export function isTaskFailed(task) {
  return task?.status === "fail" || task?.result?.success === false;
}

/**
 * Error codes that mean the account's token is no longer usable, so the only
 * way forward is reconnecting it.
 */
export const TOKEN_FAILURE_CODES = ["LI_TOKEN_INVALID", "SESSION_EXPIRED"];

/**
 * Pull the human reason out of a failed task.
 *
 * The server sends both a machine code and a sentence explaining it, nested a
 * couple of levels down in the provider envelope
 * (`result.data.data.detail`). Showing the code alone — which is what used to
 * happen — tells the user nothing and hides the fix.
 *
 * @param {Object} task - Failed post task.
 * @returns {Object} The code, the human sentence (detail), any raw provider
 *   text, and whether this needs a reconnect.
 */
export function describeTaskFailure(task) {
  const result = task?.result;
  if (!result) return { code: null, detail: null, raw: null, isTokenFailure: false };

  // A bare string result (a leaked exception message) is all we have to show.
  if (typeof result === "string") {
    return { code: null, detail: result, raw: null, isTokenFailure: false };
  }

  const inner = result.data?.data || {};
  // `error_code` at the top level is the unified one the server now sets for any
  // dead-credential failure (SESSION_EXPIRED); the nested one is the provider's own.
  const code = result.error_code || inner.error_code || result.error || result.data?.error || null;
  const detail =
    (result.error_code ? result.error : null) ||
    inner.detail ||
    inner.message ||
    result.data?.message ||
    null;
  const raw = inner.raw_message || null;

  return {
    code: code || null,
    detail: typeof detail === "string" ? detail : null,
    raw: typeof raw === "string" ? raw : null,
    isTokenFailure: TOKEN_FAILURE_CODES.includes(code),
  };
}

/**
 * Find the tasks whose token expired, so the caller can offer a reconnect.
 *
 * @param {Array<Object>} tasks - Post tasks for the run.
 * @returns {Array<Object>} One {accountId, socialType, code} per affected
 *   account.
 */
export function findTokenFailures(tasks = []) {
  return (Array.isArray(tasks) ? tasks : [])
    .filter((t) => isTaskFailed(t))
    .map((t) => ({ task: t, failure: describeTaskFailure(t) }))
    .filter(({ failure }) => failure.isTokenFailure)
    .map(({ task, failure }) => ({
      accountId: task.account_id,
      socialType: task.social_type,
      code: failure.code,
    }));
}

/**
 * Count what actually happened in a publish run.
 *
 * `done` counts tasks that finished either way, `succeeded` only the ones that
 * published — the two are not the same, and a run where every task failed must
 * never read as a completed publish.
 *
 * @param {Array<Object>} tasks - Post tasks for the run.
 * @returns {Object} Counts (total, done, succeeded, failed, pending), the
 *   allDone/anyFailed flags, and the progress percentage to render.
 */
export function summarizePublishTasks(tasks = []) {
  const list = Array.isArray(tasks) ? tasks.filter(Boolean) : [];
  const total = list.length;
  const doneList = list.filter(isTaskDone);
  const failed = doneList.filter(isTaskFailed).length;
  const done = doneList.length;
  const succeeded = done - failed;

  return {
    total,
    done,
    succeeded,
    failed,
    pending: total - done,
    allDone: total > 0 && done === total,
    anyFailed: failed > 0,
    progress: total === 0 ? 0 : Math.round((done / total) * 100),
  };
}

/**
 * Codes on a Threads task that only a reconnect fixes: a dead token, or a token without the replies permission
 * (OP#3465 / OP#3466).
 */
export const THREADS_RECONNECT_CODES = ["SESSION_EXPIRED", "THREADS_REPLIES_PERMISSION_MISSING"];

const THREAD_PART_STATUSES = ["pending", "published", "failed", "unknown"];

// Links from the provider end up in an href: only http(s) is let through.
const isWebLink = (value) => typeof value === "string" && /^https?:\/\//i.test(value);

/**
 * Read a Threads chain's progress from its task (OP#3466 `result.thread`).
 *
 * The task's own `publish_id` / `permalink` always mean part 1, but the generic permalink reader ignores failed
 * tasks — and a chain that broke after part 1 is a failed task with part 1 live — so part 1's link is taken
 * from here.
 *
 * @param {Object} task - Post task.
 * @returns {Object|null} `{ total, published, inProgress, resumable, failed, rootPermalink, parts }` with
 *   `parts: [{ position, status, permalink, error, errorCode }]`, or null when the task is not a chain.
 */
export function describeThread(task) {
  const result = task?.result;
  if (!result || typeof result !== "object") return null;
  const thread = result.thread;
  if (!thread || typeof thread !== "object" || !Array.isArray(thread.parts)) return null;

  const parts = thread.parts
    .filter((p) => p && typeof p === "object")
    .map((p, i) => ({
      position: Number.isInteger(p.position) ? p.position : i + 1,
      status: THREAD_PART_STATUSES.includes(p.status) ? p.status : "pending",
      permalink: isWebLink(p.permalink) ? p.permalink : null,
      error: typeof p.error === "string" && p.error ? p.error : null,
      errorCode: typeof p.error_code === "string" && p.error_code ? p.error_code : null,
    }))
    .sort((a, b) => a.position - b.position);

  const publishedCount = parts.filter((p) => p.status === "published").length;
  const total = Number.isInteger(thread.total) && thread.total > 0 ? thread.total : parts.length;
  const published = Number.isInteger(thread.published) ? thread.published : publishedCount;
  const root = parts.find((p) => p.position === 1);
  const rootPermalink =
    (root && root.status === "published" && root.permalink) ||
    (isWebLink(result.permalink) ? result.permalink : null);

  return {
    total,
    published,
    inProgress: result.in_progress === true,
    // The server only resumes a failed task; `resumable` is false when a part's state is unknown.
    resumable: thread.resumable === true && task.status === "fail",
    failed: parts.filter((p) => p.status === "failed" || p.status === "unknown").map((p) => p.position),
    rootPermalink,
    parts,
  };
}

/**
 * The translation key for a refused "Continue the thread" (POST post-tasks/resume-thread/{taskId}).
 *
 * @param {Object} resp - The request's answer ({ isOk, error, data: { code, reason, state } }).
 * @returns {string|null} An i18n key, or null when the request succeeded.
 */
export function describeResumeError(resp) {
  if (resp?.isOk) return null;
  const data = resp?.data && typeof resp.data === "object" ? resp.data : {};
  if (data.code === "THREAD_NOT_RESUMABLE") {
    if (data.reason === "not_a_thread") return "publishStatus.threadResumeNotAThread";
    if (data.reason === "not_failed") return "publishStatus.threadResumeNotFailed";
    if (data.reason === "not_resumable") return "publishStatus.threadResumeNotResumable";
  }
  if (data.code === "ACCOUNT_UNUSABLE") return "publishStatus.threadResumeAccountUnusable";
  if (data.code === "TASK_NOT_FOUND") return "publishStatus.threadResumeNotFound";
  return "publishStatus.threadResumeFailed";
}
